import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Space, theme } from 'antd';
import { AimOutlined, ColumnWidthOutlined } from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import GanttTaskTable from './GanttTaskTable';
import GanttBars from './GanttBars';
import { rangoDelDiagrama, columnaInicial } from './ganttGeometry';
import { COL_WIDTH, LEFT_WIDTH, MIN_COL_WIDTH } from './ganttLayout';

// Margen a la izquierda de "hoy" al centrar la vista, en px de la línea de tiempo.
const MARGEN_HOY = 120;
const ALTO_MAXIMO = 'calc(100vh - 260px)';

// Carta Gantt: la lista de tareas (izquierda, fija) y las barras (derecha) dentro de
// UN solo contenedor con scroll. Ambas mitades comparten alturas fijas, así que las
// filas quedan alineadas, y el scroll vertical es único.
//
// Al abrir, la vista se centra en "hoy" (o en el inicio si hoy queda fuera del rango).
// "Hoy" vuelve a ese punto; "Ajustar" reduce el ancho de columna para ver todo el rango.
//
// Props: las de GanttTaskTable y GanttBars, más
//   toolbar — contenido a la izquierda de la barra de herramientas (importar, agregar…).
//   hoy — 'YYYY-MM-DD' (por defecto, el día actual); inyectable para tests.
export default function GanttChart({
  tareas = [],
  dependencias = [],
  fechaComprometida = null,
  especialistas = [],
  readOnly = false,
  toolbar = null,
  hoy: hoyProp,
  onEditarItem,
  onReordenar,
  onAgregarSubtarea,
  onCambiarFechas,
}) {
  const { token } = theme.useToken();
  const scrollRef = useRef(null);
  const centrado = useRef(false);
  const [colWidth, setColWidth] = useState(COL_WIDTH);
  const [ajustado, setAjustado] = useState(false);

  const hoy = useMemo(() => hoyProp ?? dayjs().format('YYYY-MM-DD'), [hoyProp]);
  const rango = useMemo(
    () => rangoDelDiagrama(tareas, fechaComprometida, hoy),
    [tareas, fechaComprometida, hoy]
  );
  const dias = Number.isFinite(rango.dias) && rango.dias > 0 ? rango.dias : 1;

  const irAHoy = (ancho = colWidth) => {
    const el = scrollRef.current;
    if (!el) return;
    const col = columnaInicial({ hoy, desde: rango.desde, dias });
    el.scrollLeft = Math.max(0, col * ancho - MARGEN_HOY);
  };

  // Primera vez que hay tareas: centrar la vista.
  useEffect(() => {
    if (centrado.current || tareas.length === 0) return;
    centrado.current = true;
    irAHoy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tareas.length]);

  const handleAjustar = () => {
    if (ajustado) {
      setAjustado(false);
      setColWidth(COL_WIDTH);
      return;
    }
    const disponible = (scrollRef.current?.clientWidth ?? 0) - LEFT_WIDTH;
    const ancho = Math.max(MIN_COL_WIDTH, Math.min(COL_WIDTH, Math.floor(disponible / dias)));
    setAjustado(true);
    setColWidth(ancho);
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
  };

  const handleHoy = () => {
    if (ajustado) {
      setAjustado(false);
      setColWidth(COL_WIDTH);
      irAHoy(COL_WIDTH);
      return;
    }
    irAHoy();
  };

  return (
    <div>
      <div
        style={{
          marginBottom: 12,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Space wrap>{toolbar}</Space>
        <div style={{ flex: 1 }} />
        <Space>
          <Button icon={<AimOutlined />} onClick={handleHoy}>
            Hoy
          </Button>
          <Button
            icon={<ColumnWidthOutlined />}
            onClick={handleAjustar}
            type={ajustado ? 'primary' : 'default'}
          >
            {ajustado ? 'Escala normal' : 'Ajustar'}
          </Button>
        </Space>
      </div>

      <div
        ref={scrollRef}
        data-testid="gantt-scroll"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          overflow: 'auto',
          maxHeight: ALTO_MAXIMO,
          background: token.colorBgContainer,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
        }}
      >
        <GanttTaskTable
          tareas={tareas}
          dependencias={dependencias}
          fechaComprometida={fechaComprometida}
          especialistas={especialistas}
          readOnly={readOnly}
          onEditarItem={onEditarItem}
          onReordenar={onReordenar}
          onAgregarSubtarea={onAgregarSubtarea}
        />
        <GanttBars
          tareas={tareas}
          dependencias={dependencias}
          fechaComprometida={fechaComprometida}
          especialistas={especialistas}
          rango={rango}
          hoy={hoy}
          colWidth={colWidth}
          readOnly={readOnly}
          onCambiarFechas={onCambiarFechas}
          onEditarItem={onEditarItem}
        />
      </div>
    </div>
  );
}
